import { useState, useEffect, useRef, useCallback } from "react";
import { Save, Printer, Eye, EyeOff, AlignLeft, AlignCenter, AlignRight, ImagePlus, X, RotateCcw, Download, Loader2, RefreshCw } from "lucide-react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { usePrinterStatus, loadLastPrintQty } from "@/hooks/usePrinterStatus";
import { useResetPrinter } from "@/hooks/usePrintLabel";
import {
  CaseLabel,
  CaseLabelFields,
  CaseLabelFormatting,
  FieldFormatting,
  LabelFieldKey,
  LogoConfig,
  LABEL_FIELD_DEFS,
  DEFAULT_FIELD_FORMATTING,
  defaultFormatting,
  templateFormatting4x4,
  templateFormatting2x4,
  useCreateCaseLabel,
  useUpdateCaseLabel,
  useUploadCaseLabelLogo,
} from "@/hooks/useCaseLabels";
import { useToast } from "@/hooks/use-toast";
import { LabelCanvas, FONT_EM } from "./LabelCanvas";
import { PrintDialog } from "./PrintDialog";
import { PrintHistory } from "./PrintHistory";

interface CaseLabelEditorProps {
  label?: CaseLabel;
  onSaved: (id: string) => void;
  onNew?: (size: "4x4" | "2x4", formatting: CaseLabelFormatting, fromTemplate?: boolean) => void;
  initialSize?: "4x4" | "2x4";
  initialFormatting?: CaseLabelFormatting;
  initialFields?: CaseLabelFields;
  initialFolderId?: string | null;
  initialShowGhosts?: boolean;
}

const FONT_SIZE_OPTIONS = [
  { value: "small",  label: "S" },
  { value: "medium", label: "M" },
  { value: "large",  label: "L" },
  { value: "xl",     label: "XL" },
] as const;

const ALIGN_OPTIONS = [
  { value: "left",   Icon: AlignLeft   },
  { value: "center", Icon: AlignCenter },
  { value: "right",  Icon: AlignRight  },
] as const;

const FONT_FACE_OPTIONS = [
  { value: "gothic", label: "Sans" },
  { value: "roman",  label: "Serif" },
] as const;

const DEFAULT_LOGO_W = 200;

export function CaseLabelEditor({ label, onSaved, onNew, initialSize, initialFormatting, initialFields, initialFolderId, initialShowGhosts = false }: CaseLabelEditorProps) {
  const [name, setName]             = useState(label?.name ?? "");
  const [size]                       = useState<"4x4" | "2x4">(label?.label_size ?? initialSize ?? "4x4");
  const [fields, setFields]         = useState<CaseLabelFields>(label?.fields ?? initialFields ?? {});
  const [folderId] = useState<string | null>(label?.folder_id ?? initialFolderId ?? null);
  const [formatting, setFormatting] = useState<CaseLabelFormatting>(
    label?.formatting ?? initialFormatting ?? defaultFormatting(size)
  );
  const [logo, setLogo]             = useState<LogoConfig | null>(label?.logo ?? null);
  const [logoSelected, setLogoSelected] = useState(false);
  const [logoDragOver, setLogoDragOver] = useState(false);
  const [showGhosts, setShowGhosts]   = useState(initialShowGhosts);
  const [printOpen, setPrintOpen]     = useState(false);
  const [printInitQty, setPrintInitQty] = useState<number | undefined>(undefined);
  const [pdfPending, setPdfPending]     = useState(false);
  const [selectedKey, setSelectedKey]   = useState<LabelFieldKey | null>(null);
  const { status: printerStatus, ribbonPercent } = usePrinterStatus();
  const resetPrinter = useResetPrinter();
  const lastQty = loadLastPrintQty();
  const nameEditedRef = useRef(!!label);
  const fieldRefs     = useRef<Partial<Record<LabelFieldKey, HTMLDivElement | null>>>({});
  const fileInputRef  = useRef<HTMLInputElement>(null);
  const canvasBoxRef  = useRef<HTMLDivElement>(null);
  const [labelId, setLabelId]       = useState<string | undefined>(label?.id);

  const createLabel = useCreateCaseLabel();
  const updateLabel = useUpdateCaseLabel();
  const uploadLogo  = useUploadCaseLabelLogo();
  const isSaving    = createLabel.isPending || updateLabel.isPending;
  const { toast }   = useToast();

  // Cmd+P / Ctrl+P → open print dialog instead of browser print
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "p") {
        e.preventDefault();
        setPrintInitQty(undefined);
        setPrintOpen(true);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Arrow keys → nudge selected field or logo (1 dot plain, 10 dots with Shift)
  useEffect(() => {
    const LABEL_W = 812;
    const LABEL_H = size === "4x4" ? 812 : 406;
    const ARROWS  = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"];

    function onKeyDown(e: KeyboardEvent) {
      if (!ARROWS.includes(e.key)) return;
      const target = e.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) return;
      if (!selectedKey && !logoSelected) return;
      e.preventDefault();

      const step = e.shiftKey ? 10 : 1;
      const dx = e.key === "ArrowRight" ? step : e.key === "ArrowLeft" ? -step : 0;
      const dy = e.key === "ArrowDown"  ? step : e.key === "ArrowUp"   ? -step : 0;

      if (selectedKey) {
        setFormatting((prev) => {
          const fmt = prev[selectedKey] ?? DEFAULT_FIELD_FORMATTING;
          return {
            ...prev,
            [selectedKey]: {
              ...fmt,
              x: Math.round(Math.max(0, Math.min(LABEL_W, (fmt.x ?? 20) + dx))),
              y: Math.round(Math.max(0, Math.min(LABEL_H, (fmt.y ?? 20) + dy))),
            },
          };
        });
      } else if (logoSelected) {
        setLogo((prev) => prev ? {
          ...prev,
          x: Math.round(Math.max(0, Math.min(LABEL_W - prev.width,  prev.x + dx))),
          y: Math.round(Math.max(0, Math.min(LABEL_H - prev.height, prev.y + dy))),
        } : prev);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedKey, logoSelected, size]);

  // Auto-suggest name from Brand + Vintage + Variety
  useEffect(() => {
    if (nameEditedRef.current) return;
    const parts = [fields.brand, fields.vintage, fields.variety].filter(Boolean);
    if (parts.length > 0) setName(parts.join(" "));
  }, [fields.brand, fields.vintage, fields.variety]);

  // Scroll left panel to selected field
  useEffect(() => {
    if (!selectedKey) return;
    fieldRefs.current[selectedKey]?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [selectedKey]);

  const handleMove = useCallback((key: LabelFieldKey, x: number, y: number) => {
    setFieldFmt(key, { x, y });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handleSelect(key: LabelFieldKey | null) {
    setSelectedKey(key);
    if (key) setLogoSelected(false);
  }

  function setField(key: LabelFieldKey, value: string) {
    setFields((prev) => ({ ...prev, [key]: value || undefined }));
  }

  // x is the anchor point and means something different per alignment (left
  // edge / visual center / right edge). Switching alignment without this
  // would leave x's old meaning applied under the new alignment, jumping the
  // text to a different spot instead of keeping it where it visually was.
  function anchorXForAlignment(
    oldAlign: FieldFormatting["alignment"], oldX: number,
    newAlign: FieldFormatting["alignment"], widthDots: number
  ): number {
    const left = oldAlign === "center" ? oldX - widthDots / 2 : oldAlign === "right" ? oldX - widthDots : oldX;
    if (newAlign === "left") return Math.round(left);
    if (newAlign === "right") return Math.round(left + widthDots);
    return Math.round(left + widthDots / 2);
  }

  function setFieldAlignment(key: LabelFieldKey, newAlign: FieldFormatting["alignment"]) {
    const fmt = formatting[key] ?? DEFAULT_FIELD_FORMATTING;
    const text = fields[key] ?? "";
    const widthDots = text.length * (FONT_EM[fmt.font_size ?? "medium"] ?? FONT_EM.medium);
    const newX = anchorXForAlignment(fmt.alignment ?? "left", fmt.x ?? DEFAULT_FIELD_FORMATTING.x, newAlign, widthDots);
    setFieldFmt(key, { alignment: newAlign, x: newX });
  }

  function setFieldFmt(key: LabelFieldKey, updates: Partial<FieldFormatting>) {
    setFormatting((prev) => ({
      ...prev,
      [key]: { ...(prev[key] ?? DEFAULT_FIELD_FORMATTING), ...updates },
    }));
  }

  async function handleLogoFile(file: File) {
    const url = await uploadLogo.mutateAsync(file);
    const img = new Image();
    img.onload = () => {
      const aspect = img.naturalWidth / img.naturalHeight;
      const w = DEFAULT_LOGO_W;
      const h = Math.round(w / aspect);
      setLogo({ url, x: 20, y: 20, width: w, height: h });
      setLogoSelected(true);
      setSelectedKey(null);
    };
    img.src = url;
  }

  async function handleLogoFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    await handleLogoFile(file);
  }

  function handleLogoDrop(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    setLogoDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (!file || !file.type.startsWith("image/")) return;
    handleLogoFile(file);
  }

  async function handleSave() {
    const payload = {
      name: name.trim() || "Untitled Label",
      folder_id: folderId,
      label_size: size,
      fields,
      formatting,
      logo: logo ?? null,
    };
    try {
      if (labelId) {
        const updated = await updateLabel.mutateAsync({ id: labelId, ...payload });
        onSaved(updated.id);
      } else {
        const created = await createLabel.mutateAsync(payload);
        setLabelId(created.id);
        onSaved(created.id);
      }
    } catch {
      // toast handled by mutation onError
    }
  }

  async function handleDownloadPDF() {
    const el = canvasBoxRef.current;
    if (!el) return;
    setPdfPending(true);
    try {
      // Hide ghost field labels and dashed border — clean white label only
      const ghosts = el.querySelectorAll<HTMLElement>("[data-ghost]");
      ghosts.forEach((g) => { g.style.opacity = "0"; });
      const prevOutline = el.style.outline;
      el.style.outline = "none";

      const canvas = await html2canvas(el, {
        useCORS:    true,
        allowTaint: false,
        scale:      4,
        backgroundColor: "#ffffff",
      });

      ghosts.forEach((g) => { g.style.opacity = ""; });
      el.style.outline = prevOutline;
      const imgData = canvas.toDataURL("image/jpeg", 0.85);
      const widthIn  = 4;
      const heightIn = size === "4x4" ? 4 : 2;
      const pdf = new jsPDF({
        orientation: heightIn < widthIn ? "landscape" : "portrait",
        unit:        "in",
        format:      [widthIn, heightIn],
      });
      pdf.addImage(imgData, "JPEG", 0, 0, widthIn, heightIn);
      const safeName = (name.trim() || "label").replace(/[^a-z0-9_-]/gi, "-").toLowerCase();
      pdf.save(`${safeName}.pdf`);
    } catch (e) {
      toast({ title: "PDF export failed", description: String(e), variant: "destructive" });
    } finally {
      setPdfPending(false);
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b bg-background/80 backdrop-blur-sm sticky top-0 z-10 shrink-0">
        <div className="px-4 py-2.5 flex items-center gap-2">
          <div className="ml-auto flex items-center gap-1.5">
            {onNew && (
              <>
                <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">
                  New Label
                </span>
                <div className="flex rounded-md border overflow-hidden">
                  <button type="button" onClick={() => onNew("4x4", defaultFormatting("4x4"))}
                    title="Clean slate — no fields pre-positioned. Start from scratch on a 4″ × 4″ label."
                    className="px-3 h-8 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
                    Blank 4×4
                  </button>
                  <button type="button" onClick={() => onNew("2x4", defaultFormatting("2x4"))}
                    title="Clean slate — no fields pre-positioned. Start from scratch on a 4″ × 2″ label."
                    className="border-l px-3 h-8 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
                    Blank 2×4
                  </button>
                </div>
                <div className="flex rounded-md border overflow-hidden">
                  <button type="button" onClick={() => onNew?.("4x4", templateFormatting4x4(), true)}
                    title="Start a new 4″ × 4″ label with pre-positioned fields."
                    className="px-3 h-8 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
                    Template 4×4
                  </button>
                  <button type="button" onClick={() => onNew?.("2x4", templateFormatting2x4(), true)}
                    title="Start a new 4″ × 2″ label with pre-positioned fields."
                    className="border-l px-3 h-8 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
                    Template 2×4
                  </button>
                </div>
                <div className="w-px h-5 bg-border mx-0.5" />
              </>
            )}
            <Button onClick={handleSave} disabled={isSaving} size="sm" className="h-7 px-2.5 text-xs">
              <Save className="h-3 w-3 mr-1.5" />
              {isSaving ? "Saving…" : label ? "Save" : "Create"}
            </Button>

            <Button variant="outline" size="sm" className="h-7 px-2.5 text-xs" onClick={handleDownloadPDF} disabled={pdfPending}>
              {pdfPending
                ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                : <Download className="h-3 w-3 mr-1.5" />
              }
              PDF
            </Button>

            {/* Printer status + ribbon — each with a tiny label above */}
            <div className="flex items-center gap-2.5">
              <div
                className="flex flex-col items-center gap-1"
                title={printerStatus === "online" ? "Printer online (192.168.1.77)" : printerStatus === "offline" ? "Printer offline — check that the printer is powered on and connected to the network" : "Checking printer…"}
              >
                <span className="text-[9px] leading-none text-muted-foreground/50 uppercase tracking-wide">Printer</span>
                <span className={`inline-block h-2 w-2 rounded-full shrink-0 ${
                  printerStatus === "online"  ? (
                    ribbonPercent !== null && ribbonPercent < 10  ? "bg-red-500"   :
                    ribbonPercent !== null && ribbonPercent < 30  ? "bg-amber-400" :
                    "bg-green-500"
                  ) :
                  printerStatus === "offline" ? "bg-red-500" :
                  "bg-muted-foreground/40 animate-pulse"
                }`} />
              </div>
              <div className="flex flex-col items-center gap-0.5" title={ribbonPercent !== null ? `Ribbon: ${ribbonPercent}%` : "Ribbon level unknown"}>
                <span className="text-[9px] leading-none text-muted-foreground/50 uppercase tracking-wide">Ribbon</span>
                <span className={`text-[10px] font-medium tabular-nums leading-none ${
                  printerStatus !== "online" || ribbonPercent === null ? "text-muted-foreground/30" :
                  ribbonPercent < 10 ? "text-destructive" :
                  ribbonPercent < 30 ? "text-amber-500"   :
                  "text-muted-foreground"
                }`}>
                  {printerStatus === "online" && ribbonPercent !== null ? `${ribbonPercent}%` : "—%"}
                </span>
              </div>
            </div>

            <Button
              variant="ghost"
              size="sm"
              title="Cancel any stuck jobs and soft-reset the printer over the network — no need to walk over and power-cycle it"
              onClick={() => resetPrinter.mutate()}
              disabled={resetPrinter.isPending}
              className="text-muted-foreground"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${resetPrinter.isPending ? "animate-spin" : ""}`} />
              Reset Printer
            </Button>

            <Button variant="outline" size="sm" onClick={() => { setPrintInitQty(undefined); setPrintOpen(true); }}>
              <Printer className="h-3.5 w-3.5 mr-1.5" />
              Print
            </Button>

            {lastQty && (
              <Button
                variant="ghost"
                size="sm"
                title={`Quickly reprint last job — opens print dialog with ${lastQty} label${lastQty !== 1 ? "s" : ""} pre-filled`}
                onClick={() => { setPrintInitQty(lastQty); setPrintOpen(true); }}
                className="text-muted-foreground"
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                Print more
              </Button>
            )}
          </div>
        </div>
      </div>

      <PrintDialog
        open={printOpen}
        onOpenChange={setPrintOpen}
        fields={fields}
        formatting={formatting}
        size={size}
        initialQuantity={printInitQty}
        labelId={labelId}
        labelName={name.trim() || undefined}
      />

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left panel */}
        <div className="w-72 border-r overflow-y-auto p-3 shrink-0 space-y-0.5">

          {/* ── Label Name ─────────────────────────────────────────────── */}
          <div className="rounded-lg px-2.5 py-1.5 hover:bg-muted/40 transition-all cursor-default">
            <Label htmlFor="label-name" className="text-[11px] text-muted-foreground leading-none mb-1 block">
              Label Name
            </Label>
            <div className="rounded-md border border-input bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
              <Input
                id="label-name"
                value={name}
                onChange={(e) => { nameEditedRef.current = true; setName(e.target.value); }}
                placeholder="Untitled Label"
                className="h-7 text-xs border-none shadow-none focus-visible:ring-0 bg-transparent"
              />
            </div>
          </div>

          <div className="border-t my-1" />

          {/* ── Logo / Image ───────────────────────────────────────────── */}
          <div
            className={`rounded-lg px-2.5 py-1.5 transition-all cursor-default ${
              logoSelected
                ? "bg-blue-50 dark:bg-blue-950/30 ring-1 ring-blue-200 dark:ring-blue-800"
                : "hover:bg-muted/40"
            }`}
            onClick={() => { if (logo) { setLogoSelected(true); setSelectedKey(null); } }}
          >
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-[11px] text-muted-foreground leading-none">Logo / Image</Label>
              {logo && (
                <button
                  type="button"
                  title="Remove logo"
                  onClick={(e) => { e.stopPropagation(); setLogo(null); setLogoSelected(false); }}
                  className="text-muted-foreground hover:text-destructive transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>

            {logo ? (
              <div
                className="flex items-center gap-2"
                onDragOver={(e) => { e.preventDefault(); setLogoDragOver(true); }}
                onDragLeave={() => setLogoDragOver(false)}
                onDrop={handleLogoDrop}
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={logo.url}
                  alt=""
                  className={`h-10 w-16 object-contain rounded border shrink-0 transition-colors ${
                    logoDragOver ? "border-primary bg-primary/10" : "bg-muted/30"
                  }`}
                />
                <div className="flex flex-col gap-1 min-w-0">
                  <p className="text-[10px] text-muted-foreground truncate">
                    {Math.round(logo.width / 203 * 10) / 10}″ × {Math.round(logo.height / 203 * 10) / 10}″
                  </p>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                    className="text-[10px] text-primary hover:underline text-left"
                  >
                    {logoDragOver ? "Drop to replace" : "Replace image"}
                  </button>
                </div>
              </div>
            ) : (
              <div
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); if (!uploadLogo.isPending) fileInputRef.current?.click(); }}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click(); }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setLogoDragOver(true); }}
                onDragLeave={(e) => { e.stopPropagation(); setLogoDragOver(false); }}
                onDrop={(e) => { e.stopPropagation(); handleLogoDrop(e); }}
                className={`w-full flex flex-col items-center justify-center gap-1.5 h-16 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                  logoDragOver
                    ? "border-primary bg-primary/10"
                    : "border-border hover:border-primary hover:bg-muted/30"
                }`}
              >
                <ImagePlus className={`h-4 w-4 transition-colors ${logoDragOver ? "text-primary" : "text-muted-foreground"}`} />
                <span className={`text-[11px] transition-colors ${logoDragOver ? "text-primary font-medium" : "text-muted-foreground"}`}>
                  {uploadLogo.isPending ? "Uploading…" : logoDragOver ? "Drop to upload" : "Drop image or click to browse"}
                </span>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
              className="hidden"
              onChange={handleLogoFileChange}
            />
          </div>

          <div className="border-t my-1" />

          {/* ── Text / barcode fields ─────────────────────────────────── */}
          {LABEL_FIELD_DEFS.map((def) => {
            const fmt      = formatting[def.key] ?? DEFAULT_FIELD_FORMATTING;
            const dimmed   = !fmt.visible;
            const isSelected = selectedKey === def.key;

            return (
              <div
                key={def.key}
                ref={(el) => { fieldRefs.current[def.key] = el; }}
                onClick={() => { setSelectedKey(def.key); setLogoSelected(false); }}
                className={`rounded-lg px-2.5 py-1.5 transition-all cursor-default ${
                  dimmed ? "opacity-40" : ""
                } ${isSelected
                    ? "bg-blue-50 dark:bg-blue-950/30 ring-1 ring-blue-200 dark:ring-blue-800"
                    : "hover:bg-muted/40"
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <Label htmlFor={`field-${def.key}`} className="text-[11px] text-muted-foreground leading-none">
                    {def.label}
                    {def.required && <span className="text-destructive ml-0.5">*</span>}
                  </Label>
                  <button
                    type="button"
                    title={fmt.visible ? "This field will appear on the printed label — click to hide it" : "This field is hidden from the printed label — click to show it"}
                    onClick={(e) => { e.stopPropagation(); setFieldFmt(def.key, { visible: !fmt.visible }); }}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {fmt.visible ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                  </button>
                </div>

                <div className="flex items-center gap-0 rounded-md border border-input bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                  {def.prefix && (
                    <span className="shrink-0 pl-2 text-xs text-muted-foreground whitespace-nowrap select-none">
                      {def.prefix}
                    </span>
                  )}
                  <Input
                    id={`field-${def.key}`}
                    value={fields[def.key] ?? ""}
                    onChange={(e) => setField(def.key, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    placeholder={
                      def.key === "bottled_date" ? "MM.DD.YYYY" :
                      def.key === "case_pack"    ? "12 × 750mL" :
                      def.key === "upc"          ? "12-digit UPC code" : ""
                    }
                    className={`h-7 text-xs border-none shadow-none focus-visible:ring-0 bg-transparent ${def.prefix ? "pl-0" : ""}`}
                  />
                </div>

                {def.key === "upc" && (
                  <p className="text-[10px] text-muted-foreground/70 mt-1.5 leading-snug">
                    Must be exactly 12 digits (UPC-A). Renders as a scannable barcode on the label.
                  </p>
                )}

                {isSelected && def.key !== "upc" && (
                  <div className="mt-2 space-y-1.5">
                    <div className="flex items-center gap-1">
                      {FONT_SIZE_OPTIONS.map(({ value, label: lbl }) => (
                        <FmtButton key={value} active={fmt.font_size === value}
                          onClick={() => setFieldFmt(def.key, { font_size: value })}
                          className="w-6 h-5 text-[9px] font-semibold">
                          {lbl}
                        </FmtButton>
                      ))}
                      <div className="w-px h-4 bg-border mx-0.5" />
                      <FmtButton active={fmt.bold} onClick={() => setFieldFmt(def.key, { bold: !fmt.bold })}
                        className="w-6 h-5 text-[11px] font-bold">B</FmtButton>
                      <div className="w-px h-4 bg-border mx-0.5" />
                      {ALIGN_OPTIONS.map(({ value, Icon }) => (
                        <FmtButton key={value} active={fmt.alignment === value}
                          onClick={() => setFieldAlignment(def.key, value)}
                          className="w-6 h-5 flex items-center justify-center">
                          <Icon className="h-2.5 w-2.5" />
                        </FmtButton>
                      ))}
                      <div className="w-px h-4 bg-border mx-0.5" />
                      {FONT_FACE_OPTIONS.map(({ value, label: lbl }) => (
                        <FmtButton key={value} active={(fmt.font_face ?? "gothic") === value}
                          onClick={() => setFieldFmt(def.key, { font_face: value })}
                          className={`h-5 px-1.5 text-[9px] font-medium ${value === "roman" ? "font-serif" : "font-sans"}`}>
                          {lbl}
                        </FmtButton>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          <PrintHistory labelId={labelId} />
        </div>

        {/* Canvas */}
        <div className="flex-1 flex flex-col items-center justify-center gap-3 p-10 bg-muted/30">
          <LabelCanvas
            ref={canvasBoxRef}
            fields={fields}
            formatting={formatting}
            size={size}
            selectedKey={selectedKey}
            onSelect={handleSelect}
            onMove={handleMove}
            logo={logo}
            logoSelected={logoSelected}
            onLogoSelect={() => { setLogoSelected(true); setSelectedKey(null); }}
            onLogoChange={setLogo}
            showGhosts={showGhosts}
          />
          <p className="text-[10px] text-muted-foreground/40 select-none">
            Click a field to select · Drag to reposition
          </p>
        </div>
      </div>
    </div>
  );
}

function FmtButton({
  active, onClick, className = "", children,
}: {
  active: boolean; onClick: () => void; className?: string; children: React.ReactNode;
}) {
  return (
    <button type="button" onClick={onClick}
      className={`rounded transition-colors ${
        active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"
      } ${className}`}>
      {children}
    </button>
  );
}
